import axios from 'axios';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks

/**
 * Upload a large file using chunked upload to bypass nginx file size limits
 * @param {File} file - The file to upload
 * @param {string} userId - User ID
 * @param {string} jobType - 'video' or 'caption'
 * @param {Function} onProgress - Callback for progress updates (0-100)
 * @param {Function} onPhaseChange - Callback for phase changes ('uploading', 'combining', 'processing')
 * @returns {Promise<{success: boolean, jobId: string, message: string}>}
 */
export async function uploadFileInChunks(file, userId, jobType, onProgress, onPhaseChange) {
  let sessionId = null; // Track sessionId for cleanup
  
  try {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    console.log(`📦 Starting chunked upload: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB, ${totalChunks} chunks)`);

    // Phase 1: Initialize upload session
    if (onPhaseChange) onPhaseChange('uploading');
    
    const initResponse = await axios.post('/api/locations/chunked-upload/init', {
      userId,
      fileName: file.name,
      fileSize: file.size,
      totalChunks,
      jobType,
    });

    if (!initResponse.data.success) {
      throw new Error(initResponse.data.message || 'Failed to initialize upload');
    }

    sessionId = initResponse.data.sessionId;
    console.log(`✅ Upload session initialized: ${sessionId}`);

    // Phase 2: Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('sessionId', sessionId);
      formData.append('chunkIndex', i);

      let retries = 3;
      let uploaded = false;

      while (retries > 0 && !uploaded) {
        try {
          await axios.post('/api/locations/chunked-upload/chunk', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000, // 1 minute per chunk
          });

          uploaded = true;
          
          // Calculate and report progress (0-90% for upload phase)
          const progress = Math.round(((i + 1) / totalChunks) * 90);
          if (onProgress) onProgress(progress);

          console.log(`📦 Uploaded chunk ${i + 1}/${totalChunks} (${progress}%)`);

        } catch (error) {
          retries--;
          console.warn(`⚠️  Chunk ${i} upload failed, retries left: ${retries}`, error.message);
          
          if (retries === 0) {
            throw new Error(`Failed to upload chunk ${i} after 3 attempts: ${error.message}`);
          }
          
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    // Phase 3: Finalize (combine chunks and start processing)
    if (onPhaseChange) onPhaseChange('combining');
    if (onProgress) onProgress(95);

    console.log(`🔗 Finalizing upload...`);

    const finalizeResponse = await axios.post('/api/locations/chunked-upload/finalize', {
      sessionId,
    }, {
      timeout: 120000, // 2 minutes for combining
    });

    if (!finalizeResponse.data.success) {
      throw new Error(finalizeResponse.data.message || 'Failed to finalize upload');
    }

    if (onProgress) onProgress(100);
    if (onPhaseChange) onPhaseChange('processing');

    console.log(`✅ Upload complete! Job ID: ${finalizeResponse.data.jobId}`);

    return {
      success: true,
      jobId: finalizeResponse.data.jobId,
      message: finalizeResponse.data.message,
    };

  } catch (error) {
    console.error('❌ Chunked upload failed:', error);
    
    // Attempt to abort the upload session on the backend
    if (sessionId) {
      try {
        await axios.post('/api/locations/chunked-upload/abort', { sessionId });
        console.log('🗑️  Upload session aborted and cleaned up');
      } catch (abortError) {
        console.error('⚠️  Failed to abort upload session:', abortError.message);
      }
    }
    
    throw error;
  }
}

/**
 * Check if file size exceeds nginx limit (use chunked upload)
 * @param {number} fileSize - File size in bytes
 * @returns {boolean}
 */
export function shouldUseChunkedUpload(fileSize) {
  const NGINX_LIMIT = 50 * 1024 * 1024; // 50MB (conservative estimate)
  return fileSize > NGINX_LIMIT;
}
